/**
 * 时间线片段（AiTimelineSegment）操作工具集。
 *
 * <p>所有函数都是纯函数，返回新数组，不就地修改入参。
 * 用于在 SSE 流式事件中维护助手消息内"推理 / 正文 / 工具"片段的顺序与去重。</p>
 *
 * <p>注意：本模块不再处理任何 UI 等待占位符（"正在连接模型…" / "正在规划下一步…"）。
 * 这些占位由渲染层 TimelinePendingBody.vue 根据 msg.pending 与 timeline 末尾段类型
 * 自动浮现，数据层只保留真实片段（reasoning / content / tool）。</p>
 */
import { isInternalPlanJsonText, resolveAssistantVisibleText } from '@/utils/aiMessageDisplay'
import { isTimelineVisibleToolCall } from './detectors'
import type { AiTimelineSegment, AiToolCallUi, AiToolCallView, CollapsedPriorRounds } from './types'

function appendReasoningTimeline(
  timeline: AiTimelineSegment[] | undefined,
  delta: string
): AiTimelineSegment[] {
  if (!delta) {
    return [...(timeline ?? [])]
  }
  const next = [...(timeline ?? [])]
  const last = next[next.length - 1]
  if (last?.kind === 'reasoning') {
    next[next.length - 1] = { kind: 'reasoning', text: last.text + delta }
  } else {
    next.push({ kind: 'reasoning', text: delta })
  }
  return next
}

function appendContentTimeline(
  timeline: AiTimelineSegment[] | undefined,
  delta: string
): AiTimelineSegment[] {
  if (!delta) {
    return [...(timeline ?? [])]
  }
  const next = [...(timeline ?? [])]
  const last = next[next.length - 1]
  if (last?.kind === 'content') {
    next[next.length - 1] = { kind: 'content', text: last.text + delta }
  } else {
    next.push({ kind: 'content', text: delta })
  }
  return next
}

/** 主代理 content_replace：去掉 plan JSON；在尚无用户可见正文时插入首段 content，保持已有顺序。 */
function applyContentReplace(
  timeline: AiTimelineSegment[] | undefined,
  text: string
): AiTimelineSegment[] {
  const visible = resolveAssistantVisibleText(text)
  const withoutInvalid = (timeline ?? []).filter(
    (s) => !(s.kind === 'content' && isInternalPlanJsonText(s.text))
  )

  const hasVisibleContent = withoutInvalid.some(
    (s) => s.kind === 'content' && resolveAssistantVisibleText(s.text).trim()
  )

  if (!visible.trim()) {
    return withoutInvalid.filter(
      (s) => s.kind !== 'content' || resolveAssistantVisibleText(s.text).trim()
    )
  }

  if (hasVisibleContent) {
    return withoutInvalid
  }

  const lastReasoningIdx = withoutInvalid.reduce(
    (acc, s, i) => (s.kind === 'reasoning' ? i : acc),
    -1
  )
  if (lastReasoningIdx >= 0) {
    const next = [...withoutInvalid]
    next.splice(lastReasoningIdx + 1, 0, { kind: 'content', text: visible })
    return next
  }
  return [{ kind: 'content', text: visible }, ...withoutInvalid]
}

/** 追加用户可见正文（始终新片段，不合并进已有 content，避免工具阶段正文被覆盖）。 */
function appendUserVisibleContentTimeline(
  timeline: AiTimelineSegment[] | undefined,
  text: string
): AiTimelineSegment[] {
  const visible = resolveAssistantVisibleText(text)
  if (!visible.trim()) {
    return [...(timeline ?? [])]
  }
  const next = [...(timeline ?? [])]
  next.push({ kind: 'content', text: visible })
  return next
}

function toolCallRichness(call: AiToolCallUi): number {
  let score = 0
  if (!call.pending) {
    score += 8
  }
  const raw = call.resultJson ?? call.resultViewJson ?? ''
  if (raw.includes('"answers"')) {
    score += 6
  }
  if (raw.includes('"answer"') || raw.includes('"answerText"')) {
    score += 4
  }
  if (raw.includes('"question"')) {
    score += 2
  }
  if (raw.includes('questionGroupId')) {
    score += 1
  }
  return score
}

function mergeToolCallRecord(existing: AiToolCallUi, incoming: AiToolCallUi): AiToolCallUi {
  const richer = toolCallRichness(incoming) >= toolCallRichness(existing) ? incoming : existing
  const other = richer === incoming ? existing : incoming
  const keepArgs =
    richer.argumentsJson?.trim() && richer.argumentsJson.trim() !== '{}'
      ? richer.argumentsJson
      : other.argumentsJson?.trim()
        ? other.argumentsJson
        : richer.argumentsJson
  return { ...richer, argumentsJson: keepArgs ?? richer.argumentsJson, pending: false }
}

/** 同一 callId 只保留信息更完整的一条（批量答题 API 与 SSE 竞态时去重）。 */
function dedupeTimelineToolCalls(timeline: AiTimelineSegment[]): AiTimelineSegment[] {
  const bestByCallId = new Map<string, AiToolCallUi>()
  for (const seg of timeline) {
    if (seg.kind !== 'tool' || !seg.call.callId) {
      continue
    }
    const prev = bestByCallId.get(seg.call.callId)
    bestByCallId.set(
      seg.call.callId,
      prev ? mergeToolCallRecord(prev, seg.call) : seg.call
    )
  }
  if (bestByCallId.size === 0) {
    return timeline
  }

  const emitted = new Set<string>()
  const out: AiTimelineSegment[] = []
  for (const seg of timeline) {
    if (seg.kind !== 'tool' || !seg.call.callId) {
      out.push(seg)
      continue
    }
    if (emitted.has(seg.call.callId)) {
      continue
    }
    emitted.add(seg.call.callId)
    const matchedCall = bestByCallId.get(seg.call.callId)
    if (matchedCall) {
      out.push({ kind: 'tool', call: matchedCall })
    }
  }
  return out
}

function pushToolPlaceholder(
  timeline: AiTimelineSegment[] | undefined,
  running: AiToolCallUi
): AiTimelineSegment[] {
  const next = [...(timeline ?? [])]
  next.push({ kind: 'tool', call: running })
  return next
}

function mergeToolDoneIntoTimeline(
  timeline: AiTimelineSegment[] | undefined,
  tc: AiToolCallView
): AiTimelineSegment[] {
  const next = [...(timeline ?? [])]
  const idx = next.findIndex(
    (s) => s.kind === 'tool' && s.call.callId === tc.callId
  )
  const merged: AiToolCallUi = { ...tc, pending: false }
  if (idx >= 0) {
    const existing = next[idx]
    if (existing.kind === 'tool') {
      next[idx] = { kind: 'tool', call: mergeToolCallRecord(existing.call, merged) }
      return dedupeTimelineToolCalls(next)
    }
  }
  next.push({ kind: 'tool', call: merged })
  return dedupeTimelineToolCalls(next)
}

/**
 * Removes a pre-confirmation placeholder (or obsolete historical pending row).
 * Before an explicit user approval, the confirmation card is the sole UI for
 * a write operation; it must not also appear as a pending timeline entry.
 */
function removeToolCallFromTimeline(
  timeline: AiTimelineSegment[] | undefined,
  callId: string | null | undefined
): AiTimelineSegment[] {
  if (!callId) return [...(timeline ?? [])]
  return (timeline ?? []).filter(
    (segment) => segment.kind !== 'tool' || segment.call.callId !== callId
  )
}

/** 将答题 API / 批量答题返回的 toolCalls 合并进当前助手 timeline（按 callId 覆盖或追加）。 */
function mergeToolCallsIntoTimeline(
  timeline: AiTimelineSegment[] | undefined,
  toolCalls: AiToolCallView[]
): AiTimelineSegment[] {
  if (!toolCalls.length) {
    return [...(timeline ?? [])]
  }
  const next = [...(timeline ?? [])]
  for (const tc of toolCalls) {
    if (!tc.callId) {
      continue
    }
    if (!isTimelineVisibleToolCall(tc)) {
      const withoutDeferred = removeToolCallFromTimeline(next, tc.callId)
      next.splice(0, next.length, ...withoutDeferred)
      continue
    }
    const idx = next.findIndex(
      (s) => s.kind === 'tool' && s.call.callId === tc.callId
    )
    const merged: AiToolCallUi = { ...tc, pending: false }
    if (idx >= 0) {
      const existing = next[idx]
      if (existing.kind === 'tool') {
        next[idx] = { kind: 'tool', call: mergeToolCallRecord(existing.call, merged) }
        continue
      }
    }
    next.push({ kind: 'tool', call: merged })
  }
  return dedupeTimelineToolCalls(next)
}

function compactTimeline(timeline: AiTimelineSegment[]): AiTimelineSegment[] {
  return timeline.filter((s) => {
    if (s.kind === 'reasoning') {
      return s.text.trim().length > 0
    }
    if (s.kind === 'content') {
      return s.text.trim().length > 0
    }
    return true
  })
}

/** 将服务端最终正文同步到 timeline：保持 SSE 顺序，仅清理 plan JSON。 */
function syncFinalAssistantText(
  timeline: AiTimelineSegment[] | undefined,
  finalText: string
): AiTimelineSegment[] {
  const visible = resolveAssistantVisibleText(finalText)
  const normalized = (timeline ?? [])
    .map((seg): AiTimelineSegment | null => {
      if (seg.kind === 'reasoning') {
        return seg.text.trim() ? seg : null
      }
      if (seg.kind === 'content') {
        if (isInternalPlanJsonText(seg.text)) {
          return null
        }
        const vt = resolveAssistantVisibleText(seg.text)
        return vt.trim() ? { kind: 'content', text: vt } : null
      }
      return seg
    })
    .filter((s): s is AiTimelineSegment => s != null)

  const hasContent = normalized.some((s) => s.kind === 'content')
  if (!hasContent && visible.trim()) {
    normalized.push({ kind: 'content', text: visible })
  }

  return compactTimeline(normalized)
}

/**
 * 回合正常结束（COMPLETED）的折叠态视图构建：把流式时间线切成「折叠抽屉 + 最终回复」。
 *
 * <p>切分边界 = 最后一个工具段：其后（仅 reasoning / content）属于模型最后一轮回复，
 * 其前（过程播报、工具调用、已作答的提问/确认卡）整体进入折叠抽屉。正文以服务端
 * 权威终稿（run_done.assistantMessage）为准，历史过程原样保留供展开回放。</p>
 *
 * <p>单轮问答（没有工具段）抽屉为空 → 不挂载折叠入口，行为与折叠前一致。
 * 折叠条耗时以服务端下发的 durationMs 为准；缺失（历史数据/评估运行）时不折叠。</p>
 */
function buildCompletedTurnView(
  timeline: AiTimelineSegment[] | undefined,
  finalReplyText: string,
  durationMs: number | null
): { content: string; timeline: AiTimelineSegment[] | null; collapsedPriorRounds: CollapsedPriorRounds | null } {
  const visible = resolveAssistantVisibleText(finalReplyText || '')
  const segments = [...(timeline ?? [])]

  let lastToolIdx = -1
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i].kind === 'tool') {
      lastToolIdx = i
      break
    }
  }
  const priorSegments = segments.slice(0, lastToolIdx + 1)
  const finalReasoning = segments
    .slice(lastToolIdx + 1)
    .filter((seg): seg is Extract<AiTimelineSegment, { kind: 'reasoning' }> =>
      seg.kind === 'reasoning' && seg.text.trim().length > 0)

  const mainTimeline: AiTimelineSegment[] = [...finalReasoning]
  if (visible.trim()) {
    mainTimeline.push({ kind: 'content', text: visible })
  }

  const collapsedPriorRounds = visible.trim().length > 0 && durationMs != null && durationMs > 0 && priorSegments.length > 0
    ? { durationMs, segments: compactTimeline(priorSegments) }
    : null

  return {
    content: visible,
    timeline: mainTimeline.length > 0 ? compactTimeline(mainTimeline) : null,
    collapsedPriorRounds
  }
}

export {
  appendReasoningTimeline,
  appendContentTimeline,
  applyContentReplace,
  appendUserVisibleContentTimeline,
  pushToolPlaceholder,
  mergeToolDoneIntoTimeline,
  removeToolCallFromTimeline,
  mergeToolCallsIntoTimeline,
  dedupeTimelineToolCalls,
  compactTimeline,
  syncFinalAssistantText,
  buildCompletedTurnView
}
